/**
 * Real API implementation for User Invite (invite, update, delete user).
 * Single place for v3 user API error handling (message, errors object, primary-admin guard).
 */

import { apiClient } from '../apiClient';
import { serviceLogger } from '../../utils/serviceLogger';
import { normalizeServiceError } from '../../utils/serviceError';

const V3_USERS_BASE = '/api/v3/users';
const CANNOT_DELETE_PRIMARY_ADMIN_CODE = 'CANNOT_DELETE_PRIMARY_ADMIN';

/**
 * Builds and throws the appropriate Error from v3 user API error response.
 * @param {Error} error - Caught error from apiClient
 * @param {{ context: string, defaultMessage: string, handlePrimaryAdmin?: boolean }} options
 */
function handleV3UserApiError(error, options = {}) {
  const { context, defaultMessage, handlePrimaryAdmin = false } = options;
  serviceLogger.error(`[real${context}] Error:`, error);

  const data = error.response?.data;
  const errorObj = data?.error;

  if (handlePrimaryAdmin && errorObj?.code === CANNOT_DELETE_PRIMARY_ADMIN_CODE) {
    const e = new Error(
      errorObj.message || 'Cannot delete the Primary Organization Admin',
    );
    e.code = CANNOT_DELETE_PRIMARY_ADMIN_CODE;
    throw e;
  }
  if (typeof data?.message === 'string') throw new Error(data.message);
  if (data?.errors && typeof data.errors === 'object') {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    throw new Error(messages.length ? messages.join(', ') : defaultMessage);
  }
  throw normalizeServiceError(error);
}

export const inviteUser = async (requestData) => {
  try {
    const response = await apiClient.post(`${V3_USERS_BASE}/invite`, requestData);
    return response.data;
  } catch (error) {
    handleV3UserApiError(error, {
      context: 'InviteUser',
      defaultMessage: 'Failed to invite user',
    });
  }
};

export const updateUser = async (userId, requestData) => {
  try {
    const response = await apiClient.patch(`${V3_USERS_BASE}/${userId}`, requestData);
    return response.data;
  } catch (error) {
    handleV3UserApiError(error, {
      context: 'UpdateUser',
      defaultMessage: 'Failed to update user',
    });
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`${V3_USERS_BASE}/${userId}`);
    return response.data;
  } catch (error) {
    handleV3UserApiError(error, {
      context: 'DeleteUser',
      defaultMessage: 'Failed to delete user',
      handlePrimaryAdmin: true,
    });
  }
};
